<template>
    <div class="branded-split min-h-screen flex flex-col lg:flex-row">
        <!-- Left panel — branding -->
        <div
            class="flex flex-col justify-between bg-sky-500 dark:bg-sky-900 px-8 py-10 lg:w-2/5 lg:min-h-screen"
        >
            <div>
                <img
                    src="https://cdn.zenmanage.com/images/logo/png/logo_color_bgtransparent_h_cropped.png"
                    alt="Zenmanage"
                    class="h-8 brightness-0 invert"
                />

                <div class="mt-16 lg:mt-24">
                    <h1 class="text-3xl font-semibold text-white lg:text-4xl">
                        {{ heading }}
                    </h1>
                    <p
                        v-if="description"
                        class="mt-4 text-lg text-sky-100 leading-relaxed"
                    >
                        {{ description }}
                    </p>
                </div>

                <ul
                    v-if="highlights.length"
                    class="mt-10 space-y-4 hidden lg:block"
                >
                    <li
                        v-for="item in highlights"
                        :key="item"
                        class="flex items-start gap-3 text-sky-100"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            class="mt-0.5 h-5 w-5 shrink-0 text-white"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <span class="text-sm">{{ item }}</span>
                    </li>
                </ul>
            </div>

            <footer class="mt-10 hidden lg:block space-y-4">
                <p v-if="footnote" class="text-xs text-sky-200">
                    {{ footnote }}
                </p>

                <div class="text-xs text-sky-200 leading-relaxed space-y-2">
                    <p>
                        Copyright &copy; 2024-2026 Zenmanage, LLC. All rights
                        reserved.<br />
                        <a
                            href="https://zenmanage.com/legal/terms-of-service"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-white hover:underline"
                            >Terms of Service</a
                        >
                        &middot;
                        <a
                            href="https://zenmanage.com/legal/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-white hover:underline"
                            >Privacy Policy</a
                        >
                        &middot;
                        <a
                            href="https://zenmanage.com/legal/cookie-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-white hover:underline"
                            >Cookie Policy</a
                        >
                        &middot;
                        <a
                            href="https://zenmanage.com/legal/acceptable-use-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-white hover:underline"
                            >Acceptable Use Policy</a
                        >
                    </p>

                    <p>
                        Made with
                        <a
                            href="https://en.wikipedia.org/wiki/Wisconsin_cheese"
                            target="_blank"
                            rel="noopener noreferrer"
                            >🧀</a
                        >
                        in Wisconsin, USA.
                    </p>

                    <p>
                        Need help or have a question?
                        <a
                            href="mailto:hello@zenmanage.com"
                            class="text-white hover:underline"
                            >Send us a message</a
                        >
                    </p>
                </div>
            </footer>
        </div>

        <!-- Right panel — content -->
        <div
            class="flex flex-1 flex-col px-6 py-10 sm:px-10 lg:px-16 xl:px-24 bg-white dark:bg-taupe-900 dark:scheme-dark"
        >
            <div
                class="flex flex-1 flex-col justify-start lg:justify-center w-full max-w-md mx-auto"
            >
                <slot />
            </div>

            <footer
                class="lg:hidden mt-10 w-full max-w-md mx-auto text-xs text-taupe-500 dark:text-taupe-400 leading-relaxed space-y-2"
            >
                <p>
                    Copyright &copy; 2024-2026 Zenmanage, LLC. All rights
                    reserved.<br />
                    <a
                        href="https://zenmanage.com/legal/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300"
                        >Terms of Service</a
                    >
                    &middot;
                    <a
                        href="https://zenmanage.com/legal/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300"
                        >Privacy Policy</a
                    >
                    &middot;
                    <a
                        href="https://zenmanage.com/legal/cookie-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300"
                        >Cookie Policy</a
                    >
                    &middot;
                    <a
                        href="https://zenmanage.com/legal/acceptable-use-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300"
                        >Acceptable Use Policy</a
                    >
                </p>

                <p>
                    Made with
                    <a
                        href="https://en.wikipedia.org/wiki/Wisconsin_cheese"
                        target="_blank"
                        rel="noopener noreferrer"
                        >🧀</a
                    >
                    in Wisconsin, USA.
                </p>

                <p>
                    Need help or have a question?
                    <a
                        href="mailto:hello@zenmanage.com"
                        class="text-sky-500 dark:text-sky-400 hover:text-sky-600 dark:hover:text-sky-300"
                        >Send us a message</a
                    >
                </p>
            </footer>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BrandedSplitScreen',
    props: {
        heading: {
            type: String,
            required: true,
        },
        description: {
            type: String,
            default: '',
        },
        highlights: {
            type: Array,
            default: () => [],
        },
        footnote: {
            type: String,
            default: '',
        },
    },
}
</script>
